'use client';

import Icon from '@/components/ui/AppIcon';

/**
 * Footer Component (Organism)
 * 
 * Common footer displayed across all pages with support email, social sharing, and copyright information.
 * Follows Atomic Design principles as an Organism-level component.
 */

const SHARE_URL = 'https://www.offerxray.com/';
const SHARE_TEXT = 'Just discovered Offer XRay - AI-powered offer letter analysis that helps you identify risks, understand compensation, and negotiate with confidence! 🚀 Check it out:';

const getTwitterShareUrl = () => {
  const text = encodeURIComponent(`${SHARE_TEXT} ${SHARE_URL}`);
  return `https://twitter.com/intent/tweet?text=${text}`;
};

const getLinkedInShareUrl = () => {
  const url = encodeURIComponent(SHARE_URL);
  return `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
};

export default function Footer() {
  const handleTwitterShare = () => {
    window.open(getTwitterShareUrl(), '_blank', 'width=550,height=420');
  };

  const handleLinkedInShare = () => {
    window.open(getLinkedInShareUrl(), '_blank', 'width=550,height=420');
  };

  return (
    <footer className="border-t border-border py-8 bg-surface/50">
      <div className="container mx-auto px-4 text-center">
        <p className="text-sm text-text-secondary">
          Your documents are processed securely and never stored permanently
        </p>
        
        {/* Share and Support */}
        <div className="mt-6 flex items-center justify-center gap-6 flex-wrap">
          {/* Social Sharing */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-secondary font-medium">Share:</span>
            <button
              onClick={handleTwitterShare}
              className="group inline-flex items-center gap-2 px-5 py-2.5 bg-surface border border-border rounded-xl text-sm font-medium text-text-primary hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2]/50 hover:text-[#1DA1F2] transition-all duration-300 shadow-sm hover:shadow-md"
              aria-label="Share on Twitter/X"
            >
              <svg className="w-5 h-5 text-[#1DA1F2] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span>Twitter</span>
            </button>
            <button
              onClick={handleLinkedInShare}
              className="group inline-flex items-center gap-2 px-5 py-2.5 bg-surface border border-border rounded-xl text-sm font-medium text-text-primary hover:bg-[#0077B5]/10 hover:border-[#0077B5]/50 hover:text-[#0077B5] transition-all duration-300 shadow-sm hover:shadow-md"
              aria-label="Share on LinkedIn"
            >
              <svg className="w-5 h-5 text-[#0077B5] group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
              <span>LinkedIn</span>
            </button>
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-border"></div>

          {/* Support Email */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-secondary font-medium">Support:</span>
            <a 
              href="mailto:zaman.ishtiyaq@gmail.com"
              className="group inline-flex items-center gap-2 px-6 py-2.5 bg-surface border border-border rounded-xl text-sm font-medium text-text-primary hover:bg-primary/10 hover:border-primary/50 hover:text-primary transition-all duration-300 shadow-sm hover:shadow-md"
            >
              <Icon name="EnvelopeIcon" size={18} className="text-primary group-hover:scale-110 transition-transform" />
              <span>zaman.ishtiyaq@gmail.com</span>
            </a>
          </div>

          {/* Divider */}
          <div className="h-8 w-px bg-border"></div>

          {/* Product Hunt Badge */}
          <a
            href="https://www.producthunt.com/products/offer-x-ray?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-offer&#0045;x&#0045;ray"
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform hover:scale-105"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1046744&theme=light&t=1765007005144"
              alt="Offer X-Ray - Offer Letter Analysis to Spot Risks & Negotiate Confidently | Product Hunt"
              className="h-11 w-auto"
              width="250"
              height="54"
            />
          </a>
        </div>
        
        <div className="mt-4 flex items-center justify-center gap-4 text-xs flex-wrap">
          <a href="/blog" className="text-text-secondary hover:text-primary transition-colors">
            Blog
          </a>
          <span className="text-border">•</span>
          <a href="/privacy" className="text-text-secondary hover:text-primary transition-colors">
            Privacy Policy
          </a>
          <span className="text-border">•</span>
          <a href="/terms" className="text-text-secondary hover:text-primary transition-colors">
            Terms of Service
          </a>
        </div>

        <p className="text-xs text-text-secondary mt-4">
          © {new Date().getFullYear()} Offer XRay. All rights reserved.
        </p>
        <p className="text-xs text-text-secondary mt-1">
          Website by{' '}
          <a
            href="https://www.anarchylabs.in/"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors underline"
          >
            Anarchy Labs
          </a>
        </p>
      </div>
    </footer>
  );
}

